import React from 'react'
import { Input, Skeleton } from 'antd'
import {
  IDispenserOption,
  IFuelOption,
  IRefuelAccount,
  IStation,
} from './types'
import { AMOUNT_TEMPLATES, MAX_LITERS, MIN_LITERS } from './constants'
import { formatLiters } from './format'

interface IFAmountStep {
  station: IStation
  account: IRefuelAccount
  loading: boolean
  dispensers: IDispenserOption[]
  dispenser?: string
  onDispenser: (value: string) => void
  fuels: IFuelOption[]
  fuel?: IFuelOption
  onFuel: (fuel: IFuelOption) => void
  amount: string
  onAmount: (value: string) => void
  /** The account's remaining litres of the chosen fuel. */
  max: number
  validation?: string
  onTemplate: (liters: number) => void
}

/**
 * Dispenser, fuel and litres, each section unlocking the next. The station and
 * the litres about to be debited live in the modal's header and footer, so this
 * is only the three choices.
 */
const AmountStep: React.FC<IFAmountStep> = (props) => {
  const { fuel, dispenser, max } = props
  const picked = !!dispenser
  // The balance is the ceiling until it passes the product cap, which then
  // takes over — the hint names whichever one the driver is actually held to.
  const capped = max > MAX_LITERS
  const limit = capped ? MAX_LITERS : max

  return (
    <div className="refuel-step">
      {props.loading ? (
        <Skeleton active title={false} paragraph={{ rows: 4 }} />
      ) : (
        <>
          <div className="refuel-field">
            <label>Колонка</label>
            <div className="refuel-pumps">
              {props.dispensers.map((item) => {
                const state = !item.available
                  ? 'refuel-pump disabled'
                  : item.id === dispenser
                  ? 'refuel-pump active'
                  : 'refuel-pump'
                return (
                  <div
                    key={item.id}
                    className={state}
                    title={
                      item.available
                        ? undefined
                        : 'Немає пального з цього акаунта'
                    }
                    onClick={() => item.available && props.onDispenser(item.id)}
                  >
                    <span className="refuel-pump__label">Колонка</span>
                    <span className="refuel-pump__number">{item.id}</span>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="refuel-field">
            <label>Пальне</label>
            <div className={picked ? 'refuel-fuels' : 'refuel-fuels locked'}>
              {props.fuels.map((item) => {
                const state = !item.available
                  ? 'refuel-fuel disabled'
                  : item.goodsGuid === fuel?.goodsGuid
                  ? 'refuel-fuel active'
                  : 'refuel-fuel'
                return (
                  <div
                    key={item.goodsGuid}
                    className={state}
                    onClick={() => item.available && props.onFuel(item)}
                  >
                    <span className="refuel-fuel__mark" />
                    <div className="refuel-fuel__body">
                      <span className="refuel-fuel__name">{item.label}</span>
                      {item.balance !== undefined && (
                        <span className="refuel-fuel__note">
                          залишок {formatLiters(item.balance)} л
                        </span>
                      )}
                    </div>
                    {!item.available && (
                      <span className="refuel-fuel__off">{item.reason}</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className={picked ? 'refuel-amount' : 'refuel-amount locked'}>
            <label>Скільки заправити</label>

            <Input
              type="number"
              min="0"
              max={MAX_LITERS}
              step="0.01"
              inputMode="decimal"
              className="refuel-amount__input"
              value={props.amount}
              onChange={(e) => props.onAmount(e.target.value)}
              suffix="л"
            />

            <div className="refuel-templates">
              {AMOUNT_TEMPLATES.map((value) => (
                <span
                  key={value}
                  className={max < value ? 'disabled' : ''}
                  onClick={() => max >= value && props.onTemplate(value)}
                >
                  {value} л
                </span>
              ))}
            </div>
          </div>

          {picked && fuel && (
            <>
              {props.validation ? (
                <div className="refuel-summary__error">{props.validation}</div>
              ) : (
                <div className="refuel-summary__hint">
                  {`Від ${MIN_LITERS} л до ${formatLiters(limit)} л — мінімальний літраж `}
                  {capped
                    ? 'та максимум за одну заправку.'
                    : `та залишок ${fuel.label} на цьому акаунті.`}
                </div>
              )}
            </>
          )}

          {picked && !fuel && (
            <div className="refuel-summary__hint centered">
              Оберіть вид пального — від нього залежить ліміт
            </div>
          )}

          {!picked && (
            <div className="refuel-summary__hint centered">
              Спершу оберіть колонку — паливо й сума розблокуються після цього
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AmountStep
